import { Avatar } from '@mui/material'
import { styled } from '@mui/material/styles'
import { colorPalette } from '../../style/colorPalette'
import { userBasicCache } from './userBasicCache'
import { useBasicByCode } from './hooks/useBasicByCode'

// WbsItemLinkChip の見た目（白地に細い枠）に揃える。同じ本文の中に並ぶチップで、
// 枠や余白が違うと同じ役割の要素に見えないため。フォントサイズは em 指定にして、
// 本文（MarkdownContent）側の文字サイズにそのまま追従させる。
//
// WbsItemLinkChip と違い a 要素にはしない。このチップは遷移先を持たないため、
// クリックしても何も起きないリンクを見せないという方針は WbsItemStatusLinkChip と同じ。
const ChipRoot = styled('span')({
  display: 'inline-flex',
  alignItems: 'center',
  gap: 3,
  verticalAlign: 'middle',
  maxWidth: '100%',
  padding: '0 6px',
  borderRadius: 4,
  border: `1px solid ${colorPalette.monotone[2]}`,
  backgroundColor: colorPalette.monotone[0],
  color: colorPalette.monotone[10],
  fontSize: '0.92em',
  fontWeight: 600,
  lineHeight: 1.6,
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
})

const ICON_SIZE = 14

// 取得中はアイコンが無く、取得後に現れる。幅を先に確保しておき、その出現でラベルの位置が
// 左右にずれる（レイアウトシフトする）のを避ける。
const IconSlot = styled('span')({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: ICON_SIZE,
  height: ICON_SIZE,
  flexShrink: 0,
})

// ユーザーのアイコンを丸く出す共通ラッパーは components/ に無く（TenantAvatar はテナント用）、
// KanbanV2 のカードなど既存の担当者表示も MUI の Avatar を直接使っている。ここもそれに倣う。
//
// アイコン URL が未設定のユーザーでは children に渡した氏名の頭文字が表示される。その配色は
// 既存の担当者アバター（KanbanV2 の UserAvatar / ProjectMemberSelector など）と揃え、背景は
// MUI 既定のままにして文字色だけ monotone[10] にする。同じ人物が画面上の別の場所と違う色で
// 出ないようにするため。
const UserAvatar = styled(Avatar)({
  width: ICON_SIZE,
  height: ICON_SIZE,
  fontSize: 9,
  color: colorPalette.monotone[10],
})

// 表のセル内では列幅が中身から決まる（table-layout: auto）ため、ChipRoot の '100%' は
// 上限として解決されない。パーセンテージと違い絶対値の max-width は列幅の計算に効くので、
// 幅の上限はラベル側に絶対値で置く。em にして本文の文字サイズに追従させる。
// 氏名は WBS アイテムの名称ほど長くならないため、WbsItemLinkChip より短い上限にする。
const LABEL_MAX_WIDTH = '12em'

// minWidth: 0 が無いと flex アイテムの既定 min-width: auto でテキストの全長まで
// しか縮まず、overflow/textOverflow を書いても省略記号が出ない。
const Label = styled('span')({
  minWidth: 0,
  maxWidth: LABEL_MAX_WIDTH,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
})

export type UserLinkChipProps = {
  code: string
  label: string
}

/**
 * 応答本文の Markdown リンク `[氏名](user:コード)` をチップ表示に差し替えるコンポーネント。
 *
 * 表示名もアイコンもコードから取得した実体を使う。WbsItemLinkChip がラベルに本文の名称を
 * 使う（モデルが文脈に合わせて短縮した名称を書いていることがある）のと違い、人の名前は
 * 短縮や言い換えが起きると別人に読めてしまうため、正式な氏名だけを出す。取得できるまでは
 * 本文に書かれた文字で代用する。
 *
 * 取得できない（404 = モデルの幻覚、403 = 参照権限なし）場合は、実在するかのような見た目の
 * チップを出さず本文と同じプレーンテキストにする（useBasicByCode 参照）。実在しない人物を
 * それらしいアイコン付きで見せないことが、ユーザーのチップでは特に重要になる。
 *
 * ホバーカードは付けない。所属や役割まで見せる価値はあるが、このチップの役割は本文中の
 * 「誰」を目で拾いやすくすることに限る（機能を持たせない方針）。
 */
export const UserLinkChip = ({ code, label }: UserLinkChipProps) => {
  const lookup = useBasicByCode(userBasicCache, code)

  // 取得できなかったコードをそれらしいチップで見せてはいけない（モデルの幻覚の可能性がある、
  // または参照権限がなく本当は実在を確認できない）。地の文と区別のつかないプレーンテキストにする。
  if (lookup.status === 'unavailable') {
    return <>{label}</>
  }

  const displayName = lookup.status === 'resolved' ? lookup.basic.name : label
  return (
    <ChipRoot>
      <IconSlot>
        {lookup.status === 'resolved' && (
          <UserAvatar variant="circular" src={lookup.basic.iconUrl}>
            {displayName.charAt(0)}
          </UserAvatar>
        )}
      </IconSlot>
      <Label>{displayName}</Label>
    </ChipRoot>
  )
}
